import type { MultilineElementTransformer } from "@lexical/markdown";
import { $createParagraphNode, $createTextNode, $isElementNode } from "lexical";
import {
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  TableNode,
  TableRowNode,
  TableCellNode,
} from "@lexical/table";
import { TableWrapperNode } from "../nodes/TableWrapperNode";

export const TABLE: MultilineElementTransformer = {
  type: "multiline-element",
  dependencies: [TableWrapperNode, TableNode],

  export: (node, exportChildren) => {
    if (node instanceof TableWrapperNode) {
      const child = node.getFirstChild();
      if (child instanceof TableNode) node = child;
      else return null;
    }

    if (!(node instanceof TableNode)) return null;

    const rows: string[] = [];

    const rowNodes = node.getChildren();
    for (const row of rowNodes) {
      if (!(row instanceof TableRowNode)) continue;

      const cellTexts: string[] = [];
      const cellNodes = row.getChildren();

      for (const cell of cellNodes) {
        if (!(cell instanceof TableCellNode)) continue;

        const paragraphs = cell.getChildren();
        const paragraphTexts = paragraphs
          .filter(p => $isElementNode(p))
          .map(p => {
            const raw = exportChildren(p);
            return raw.replace(/\n/g, "<br />").trim();
          });

        const cellText = paragraphTexts.join("<br />");
        cellTexts.push(cellText);
      }

      rows.push(`| ${cellTexts.join(" | ")} |`);
    }

    if (rows.length > 1) {
      const headerSeparator = `|${rows[0]
        ?.split("|")
        .slice(1, -1)
        .map(() => " --- ")
        .join("|")}|`;
      rows.splice(1, 0, headerSeparator);
    }

    const colWidths = node.getColWidths() ?? [];
    const rowHeights = rowNodes.map(row =>
      row instanceof TableRowNode ? (row.getHeight() ?? 0) : 0,
    );
    const meta = `<!--@meta: colWidths=${colWidths.join(",")}; rowHeights=${rowHeights.join(",")}-->`;
    rows.push(meta);
    rows.push("");
    return rows.join("\n");
  },

  regExpStart: /^ *\|(.+?)\| *$/,
  regExpEnd: /^ *\|([-:| ]+)\| *$/,

  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex }) => {
    const headerLine = lines[startLineIndex];
    const dividerLine = lines[startLineIndex + 1];

    if (!headerLine) return null;
    const isDivider = dividerLine?.match(/^\s*\|(?:\s*[-:]+\s*\|)+\s*$/);

    const headerCells = headerLine.trim().split("|").filter(Boolean);
    const bodyLines = lines.slice(startLineIndex + (isDivider ? 2 : 1));

    const table = $createTableNode();
    const headerRow = $createTableRowNode();

    for (const cellText of headerCells) {
      const cell = $createTableCellNode(1);
      const lines = cellText.split(/<br\s*\/?>|\n/g);
      for (const line of lines) {
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(line.trim()));
        cell.append(paragraph);
      }
      headerRow.append(cell);
    }

    table.append(headerRow);

    let lineCount = 0;

    for (const line of bodyLines) {
      if (!line.startsWith("|")) break;

      const row = $createTableRowNode();
      const cellTexts = line.trim().split("|").filter(Boolean);

      for (const cellText of cellTexts) {
        const cell = $createTableCellNode();
        const lines = cellText.split(/<br\s*\/?>|\n/g);
        for (const line of lines) {
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(line.trim()));
          cell.append(paragraph);
        }
        row.append(cell);
      }

      table.append(row);
      lineCount++;
    }

    const metaLine = lines[startLineIndex + (isDivider ? 2 : 1) + lineCount];
    if (metaLine?.startsWith("<!--@meta:")) {
      const match = /colWidths=([\d,]+); rowHeights=([\d,]+)/.exec(metaLine);
      const colWidths = match?.[1]?.split(",").map(s => parseInt(s, 10));
      const rowHeights = match?.[2]?.split(",").map(s => parseInt(s, 10));

      if (colWidths) {
        table.setColWidths(colWidths);
      }

      const rowNodes = table.getChildren();
      if (rowHeights && rowHeights.length === rowNodes.length) {
        for (let i = 0; i < rowNodes.length; i++) {
          const row = rowNodes[i];
          if (row instanceof TableRowNode) {
            row.setHeight(rowHeights[i]);
          }
        }
      }

      lineCount += 1;
    }

    const wrapper = new TableWrapperNode();
    wrapper.append(table);
    rootNode.append(wrapper);
    rootNode.append($createParagraphNode());
    return [true, startLineIndex + (isDivider ? 2 : 1) + lineCount + 1];
  },

  replace: () => false,
};
